/*
 * File: core.ts
 * File Created: 2024-01-11 02:07:47
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-11-11 09:26:45
 * Modified By: Val Liu
 * -----
 */

import { MetaAttribute } from "@root/core";
import lang from "@root/core/i18n/core";

export interface IFormComponentAttr {
    row: any;
    name: string;
    value: any;
    meta: MetaAttribute;
}

/// there are four FormState:
/// 1) true, showing green "checked" mark.
/// 2) false, showing red "cross" mark.
/// 3) null, passed and showing nothing.
/// 4) undefined, passed and going to next pipeline.
/// "undefined" should be returned if no other requirement.
export type IFormState =
    | true /// valid value
    | false /// invalid value
    | null /// passed. ignore the rest pipeline.
    | undefined; /// passed. but go through the rest pipeline.

export type IFormValidateResult =
    | {
          state: IFormState;
          message: string | null;
      }
    | null /// passed. ignore the rest pipeline.
    | undefined; /// passed. but go through the rest pipeline.

export function makeDefaultFormValidateResult(): NonNullable<IFormValidateResult> {
    return { state: undefined, message: null };
}

export interface ICreateValidatorOption {
    /// validator: true if passed, false if blocked.
    validator: (o: IFormComponentAttr) => boolean;
    /// the failed message.
    message: string | { (o: IFormComponentAttr): string };
    /// the success message. (optional)
    success?: string | { (o: IFormComponentAttr): string };
}
export function createValidator(
    option: ICreateValidatorOption,
): (o: IFormComponentAttr) => IFormValidateResult {
    return (o: IFormComponentAttr): IFormValidateResult => {
        const { validator, message: msg, success } = option;
        let state: NonNullable<IFormValidateResult>["state"] = undefined;
        let message: NonNullable<IFormValidateResult>["message"] = null;

        do {
            if (validator(o) === false) {
                state = false;
                message = typeof msg === "string" ? msg : msg(o);
                break;
            }

            /// handle success message
            if (success) {
                state = true;
                message = typeof success === "string" ? success : success(o);
                break;
            }
        } while (0);

        return { state, message };
    };
}

export type ICreateRequiredValidatorOption = Pick<ICreateValidatorOption, "validator">;
export function createRequiredValidator(option: ICreateRequiredValidatorOption) {
    return createValidator({
        ...option,
        message: "w_RequiredField",
    });
}
